import { z } from "zod";
import { VALID_IMAGE_EXTENSIONS } from "../constants";

export enum EBlogStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED"
}

export enum ETagsVisibility {
  PUBLIC = "PUBLIC",
  INTERNAL = "INTERNAL"
}

export enum EAuthorStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE"
}

// Blog Author Interface
export interface IBlogAuthor {
  id: string;
  slug: string;
  name: string;
  profileImage?: string;
  coverImage?: string;
  bio?: string;
  website?: string;
  location?: string;
  facebook?: string;
  twitter?: string;
  metaTitle?: string;
  metaDescription?: string;
  postCount?: number;
  status?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// Blog Tag Interface  
export interface IBlogTag {
  id?: string;
  name: string;
  slug: string;
  description?: string;
  featureImage?: string;
  visibility?: ETagsVisibility;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// Blog Interface
export interface IBlog {
  id: string;
  title: string;
  slug: string;
  html: string;
  featureImage: string;
  readingTime?: number;
  metaTitle: string;
  metaDescription: string;
  author: IBlogAuthor;
  tags: IBlogTag[];
  status: EBlogStatus;
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// Blog List Item (for cards view)
// Note: Author info is only available in single blog API, not in list API
export interface IBlogListItem {
  id: string;
  title: string;
  slug: string;
  featureImage: string;
  readingTime?: number;
  tags: IBlogTag[];
  status: EBlogStatus;
  publishedAt?: string | null;
  // author is not included in list API response
}

// Blog API Response
export interface IBlogApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: {
    items: IBlogListItem[];
    meta: {
      totalCount: number;
      pageSize: number;
      limit: number;
      currentPage: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

// Single Blog API Response
export interface ISingleBlogApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: IBlog;
}

// Create Blog API Response
export interface ICreateBlogApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: {
    id: string;
    slug: string;
    title: string;
    html: string;
    featureImage: string;
    readingTime: number | null;
    metaTitle: string;
    metaDescription: string;
    tagIds: string[];
    authorId: string;
    status: EBlogStatus;
    publishedAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
}

// Zod Schemas for validation
export const blogStatusSchema = z.nativeEnum(EBlogStatus);

export const createBlogSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters"),
  slug: z.string().min(2, "Slug must be at least 2 characters"),
  html: z.string().min(10, "Content must be at least 10 characters"),
  featureImage: z.string().url("Feature image must be a valid URL"),
  readingTime: z.number().min(1).optional(),
  metaTitle: z.string().min(5, "Meta title must be at least 5 characters"),
  metaDescription: z.string().min(20, "Meta description must be at least 20 characters"),
  authorId: z.string().uuid("Author is required"),
  tags: z.array(z.string().uuid()).min(1, "Tags must be atleast 1"),
  status: blogStatusSchema,
  publishedAt: z.coerce.date().nullish(),
});

export const updateBlogSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters").optional(),
  slug: z.string().min(2, "Slug must be at least 2 characters").optional(),
  html: z.string().min(10, "Content must be at least 10 characters").optional(),
  featureImage: z.string().url("Feature image must be a valid URL").optional(),
  readingTime: z.number().min(1).optional(),
  metaTitle: z.string().min(5, "Meta title must be at least 5 characters").optional(),
  metaDescription: z.string().min(20, "Meta description must be at least 20 characters").optional(),
  authorId: z.string().uuid("Author is required").optional(),
  tags: z.array(z.string().uuid()).min(1, "Tags must be an atleast 1").nullish(),
  status: blogStatusSchema.optional(),
  publishedAt: z.coerce.date().nullish(),
});

export const blogQuerySchema = z.object({
  searchTerm: z.string().optional(),
  tagSlug: z.string().optional(),
  random: z.boolean().optional(),
  authorId: z.string().optional(),
  status: blogStatusSchema.optional(),
  page: z.number().min(1).default(1),
  limit: z.number().min(1).max(100).default(10),
  excludeSlug: z.string().optional(),
});

// Create Author Schema - Match backend expectations
export const createAuthorSchema = z.object({
  slug: z.string().min(2, "Slug must be at least 2 characters"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  metaTitle: z.string().min(5, "Meta title must be at least 5 characters"),
  metaDescription: z.string().min(20, "Meta description must be at least 20 characters"),
  profileImage: z.string().url("Profile image must be a valid URL").optional(),
  coverImage: z.string().url("Cover image must be a valid URL").optional(),
  bio: z.string().optional(),
  website: z.string().url("Website must be a valid URL").optional(),
  location: z.string().optional(),
  facebook: z.string().optional(),
  twitter: z.string().optional(),
  status: z.nativeEnum(EAuthorStatus).default(EAuthorStatus.ACTIVE),
});

// Create Tag Schema - Match backend expectations  
export const createTagSchema = z.object({
  slug: z.string().min(2, "Slug must be at least 2 characters"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  metaTitle: z.string().min(2, "Meta title must be at least 2 characters"),
  metaDescription: z.string().min(2, "Meta description must be at least 2 characters"),
  featureImage: z.string().url("Feature image must be a valid URL").optional(),
  visibility: z.nativeEnum(ETagsVisibility).default(ETagsVisibility.INTERNAL),
  description: z.string().optional(),
});

export const imageUrlSchema = z
    .string()
    .url("Must be a valid URL")
    .refine(
        url => {
            try {
                const urlObj = new URL(url);
                const pathname = urlObj.pathname.toLowerCase();

                return VALID_IMAGE_EXTENSIONS.some(ext => pathname.endsWith(ext));
            } catch {
                return false;
            }
        },
        {
            message:
                "URL must end with a valid image extension (.jpg, .jpeg, .png, .gif, .webp, .svg, .bmp, .ico, .tiff, .tif)",
        },
    );

// Simple form schemas for modals
export const authorFormSchema = z.object({
  slug: z.string().min(2, "Slug must be at least 2 characters"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  profileImage: imageUrlSchema.optional(),
  coverImage: imageUrlSchema.optional(),
  bio: z.string().optional(),
  website: z.string().optional(),
  location: z.string().optional(),
  facebook: z.string().optional(),
  twitter: z.string().optional(),
  metaTitle: z.string().min(5, "Meta title must be at least 5 characters"),
  metaDescription: z.string().min(20, "Meta description must be at least 20 characters"),
  status: z.nativeEnum(EAuthorStatus),
});

export const tagFormSchema = z.object({
  slug: z.string().min(2, "Slug must be at least 2 characters"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  featureImage: z.string().optional(),
  visibility: z.nativeEnum(ETagsVisibility),
  metaTitle: z.string().min(2, "Meta title must be at least 2 characters"),
  metaDescription: z.string().min(2, "Meta description must be at least 2 characters"),
});

// Type exports for the schemas
export type ICreateBlogRequest = z.infer<typeof createBlogSchema>;
export type IUpdateBlogRequest = z.infer<typeof updateBlogSchema>;
export type IBlogQueryParams = z.infer<typeof blogQuerySchema>;
export type ICreateAuthorRequest = z.infer<typeof createAuthorSchema>;
export type ICreateTagRequest = z.infer<typeof createTagSchema>;
export type IAuthorFormData = z.infer<typeof authorFormSchema>;
export type ITagFormData = z.infer<typeof tagFormSchema>;
